import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import api, { refreshAccessToken } from '../utils/api'

export const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      agent: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      signIn: async (credentials) => {
        set({ isLoading: true, error: null })
        
        try {
          const response = await api.post('/admin/login', credentials)
          const { agent, token, refreshToken: refresh_token } = response.data
          
          set({
            agent,
            token,
            refreshToken: refresh_token,
            isAuthenticated: true,
            isLoading: false,
            error: null
          })

          // Store tokens in localStorage for API interceptor
          localStorage.setItem('agent_token', token)
          localStorage.setItem('agent_refresh_token', refresh_token)
          localStorage.setItem('agent_user', JSON.stringify(agent))

          // Set token for future requests
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`
          
          return { success: true }
        } catch (error) {
          const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Sign in failed'
          set({
            isLoading: false,
            error: errorMessage,
            isAuthenticated: false,
            agent: null,
            token: null,
            refreshToken: null
          })
          return { success: false, error: errorMessage }
        }
      },

      signUp: async (agentData) => {
        set({ isLoading: true, error: null })
        
        try {
          const response = await api.post('/admin/register', agentData)
          const { agent, token, refreshToken: refresh_token } = response.data
          
          set({
            agent,
            token,
            refreshToken: refresh_token,
            isAuthenticated: true,
            isLoading: false,
            error: null
          })

          // Store tokens in localStorage for API interceptor
          localStorage.setItem('agent_token', token)
          localStorage.setItem('agent_refresh_token', refresh_token)
          localStorage.setItem('agent_user', JSON.stringify(agent))

          // Set token for future requests
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`
          
          return { success: true }
        } catch (error) {
          const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Sign up failed'
          set({
            isLoading: false,
            error: errorMessage,
            isAuthenticated: false,
            agent: null,
            token: null,
            refreshToken: null
          })
          return { success: false, error: errorMessage }
        }
      },

      refreshAccessToken: () => refreshAccessToken(),

      signOut: async () => {
        // Best-effort server logout (clears the cookie); never blocks local sign-out
        if (get().token) {
          api.post('/admin/logout').catch(() => {})
        }

        set({
          agent: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          error: null
        })

        // Remove tokens from localStorage
        localStorage.removeItem('agent_token')
        localStorage.removeItem('agent_refresh_token')
        localStorage.removeItem('agent_user')

        // Remove token from API headers
        delete api.defaults.headers.common['Authorization']
      },

      updateAgent: (agentData) => {
        set(state => ({
          agent: { ...state.agent, ...agentData }
        }))
        
        // Update localStorage
        const updatedAgent = { ...get().agent, ...agentData }
        localStorage.setItem('agent_user', JSON.stringify(updatedAgent))
      },

      clearError: () => {
        set({ error: null })
      },

      // Initialize auth state
      initialize: () => {
        const { token, refreshToken } = get()
        
        // Check if tokens exist in localStorage (for page refresh)
        const storedToken = localStorage.getItem('agent_token')
        const storedRefreshToken = localStorage.getItem('agent_refresh_token')
        const storedUser = localStorage.getItem('agent_user')
        
        if (storedToken && storedRefreshToken) {
          try {
            const agent = storedUser ? JSON.parse(storedUser) : null
            
            set({
              token: storedToken,
              refreshToken: storedRefreshToken,
              agent,
              isAuthenticated: true
            })
            
            api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`
          } catch (error) {
            console.error('Failed to parse stored user data:', error)
            get().signOut()
          }
        } else if (token) {
          // Use persisted token
          localStorage.setItem('agent_token', token)
          if (refreshToken) {
            localStorage.setItem('agent_refresh_token', refreshToken)
          }
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`
        }
      },

      // Check if token is expired (basic check)
      isTokenExpired: () => {
        const { token } = get()
        if (!token) return true
        
        try {
          const payload = JSON.parse(atob(token.split('.')[1]))
          const currentTime = Date.now() / 1000
          return payload.exp < currentTime
        } catch (error) {
          return true
        }
      }
    }),
    {
      name: 'digitaltek-agent-auth',
      partialize: (state) => ({
        agent: state.agent,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
)

// Make auth store available globally for API interceptor
window.useAuthStore = useAuthStore

// Initialize auth on app start
useAuthStore.getState().initialize()
